// Per-discipline Leveling editor.
//
// A single Junior <-> Executive seniority slider per (discipline, channel).
// The slider value is a `shift` in [-1, +1] (0 = Balanced / model default).
// It marginally tilts the level composition toward junior or senior roles
// while the engine preserves the discipline/channel total FTE (see
// resolveLevelWeights). Multi-channel disciplines (e.g. Investment) expose one
// independent slider per channel, mirroring the OGS tab. Level groups with no
// base weight in the model stay empty, so they are shown dimmed.

import { useMemo } from 'react';
import clsx from 'clsx';
import { levelMixPreview, LEVEL_GROUPS } from '@rc/engine';
import type { Discipline, LevelMixOverride, Model } from '@rc/engine/types';

interface Props {
  discipline: Discipline;
  model: Model;
  configs: LevelMixOverride[];
  onChange: (next: LevelMixOverride) => void;
}

/** Distinct channels the engine will produce cells for (from spendPerFte). */
function channelsForDiscipline(
  model: Model,
  disciplineId: string,
): { id: string; label: string }[] {
  const ids = Array.from(
    new Set(
      (model.spendPerFte || [])
        .filter((r) => r.disciplineId === disciplineId)
        .map((r) => r.channelId),
    ),
  );
  if (ids.length === 0) ids.push('all');
  return ids.map((id) => ({
    id,
    label:
      id === 'all'
        ? 'All channels'
        : (model.channels || []).find((c) => c.id === id)?.label || id,
  }));
}

function sameChannel(a: string | undefined, b: string | undefined): boolean {
  return (a ?? 'all') === (b ?? 'all');
}

// Shared label column width so the Junior<->Executive sliders line up in one
// column across every discipline and channel row.
const LABEL_W = 'w-44';

export default function LevelMixRow({ discipline, model, configs, onChange }: Props) {
  const channels = channelsForDiscipline(model, discipline.id);
  const multiChannel = channels.length > 1;

  const configFor = (channelId: string): LevelMixOverride | undefined =>
    configs.find(
      (c) => c.disciplineId === discipline.id && sameChannel(c.channelId, channelId),
    );

  if (!multiChannel) {
    const ch = channels[0];
    return (
      <LevelMixChannelLine
        label={discipline.label}
        variant="card"
        disciplineId={discipline.id}
        channelId={ch.id}
        model={model}
        value={configFor(ch.id)}
        onChange={onChange}
      />
    );
  }

  return (
    <div>
      <div className="px-3 text-sm font-medium text-slate-800 mb-1">
        {discipline.label}
        <span className="ml-2 text-[11px] font-normal text-slate-400">per channel</span>
      </div>
      <div className="space-y-1">
        {channels.map((ch) => (
          <LevelMixChannelLine
            key={ch.id}
            label={ch.label}
            variant="row"
            disciplineId={discipline.id}
            channelId={ch.id}
            model={model}
            value={configFor(ch.id)}
            onChange={onChange}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// A single compact leveling line for one (discipline, channel).
// ---------------------------------------------------------------------------
function LevelMixChannelLine({
  label,
  variant,
  disciplineId,
  channelId,
  model,
  value,
  onChange,
}: {
  label: string;
  variant: 'card' | 'row';
  disciplineId: string;
  channelId: string;
  model: Model;
  value: LevelMixOverride | undefined;
  onChange: (next: LevelMixOverride) => void;
}) {
  const shift = Number(value?.shift) || 0;
  const adjusted = shift !== 0;

  const preview = useMemo(
    () => levelMixPreview(model, disciplineId, channelId, shift),
    [model, disciplineId, channelId, shift],
  );

  // Slider works in whole percent [-100..100] mapped to shift [-1..1].
  const sliderValue = Math.round(shift * 100);

  const handleSlider = (raw: string) => {
    const pct = Math.max(-100, Math.min(100, Number(raw) || 0));
    onChange({ disciplineId, channelId, shift: pct / 100 });
  };

  const reset = () => onChange({ disciplineId, channelId, shift: 0 });

  return (
    <div
      className={clsx(
        'flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border px-3 py-1.5',
        variant === 'card'
          ? adjusted
            ? 'border-brand-purple-300 bg-brand-purple-50/30'
            : 'border-slate-200'
          : adjusted
            ? 'border-brand-purple-200 bg-brand-purple-50/40'
            : 'border-transparent',
      )}
    >
      <span
        className={clsx(
          'truncate shrink-0',
          LABEL_W,
          variant === 'card' ? 'text-sm font-medium text-slate-800' : 'text-xs text-slate-600',
        )}
      >
        {label}
      </span>

      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wide text-slate-400 w-12 text-right">
          Junior
        </span>
        <div className="flex flex-col items-center">
          <input
            type="range"
            min={-100}
            max={100}
            step={5}
            value={sliderValue}
            onChange={(e) => handleSlider(e.target.value)}
            onDoubleClick={reset}
            className="w-36 accent-brand-purple-600"
            title="Seniority tilt — double-click to reset to Balanced"
          />
          <div className="flex w-36 justify-between text-[9px] leading-none text-slate-400 -mt-0.5">
            <span>▲</span>
            <span className="text-slate-500 font-medium">Balanced</span>
            <span>▲</span>
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-wide text-slate-400 w-14">
          Executive
        </span>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        {LEVEL_GROUPS.map((g) => {
          const active = preview.active[g.id];
          const pct = Math.round((preview.groups[g.id] || 0) * 100);
          return (
            <span
              key={g.id}
              className={clsx(
                'rounded border px-1.5 py-0.5 text-[11px] tabular-nums',
                active
                  ? 'bg-white text-slate-600 border-slate-200'
                  : 'bg-slate-50 text-slate-300 border-slate-100',
              )}
              title={g.label}
            >
              {g.name} {pct}%
            </span>
          );
        })}
      </div>

      {adjusted && (
        <button
          type="button"
          onClick={reset}
          className="text-[11px] text-brand-purple-600 hover:text-brand-purple-800 hover:underline shrink-0"
        >
          Reset
        </button>
      )}
    </div>
  );
}
